import { useIsMutating } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { SpinnerCircular } from "spinners-react";
import Input from "../../../components/CommonComponents/Input/Input";
import Select from "../../../components/CommonComponents/Select/Select";
import Switch from "../../../components/CommonComponents/Switch/Switch";
import { formTypes } from "../../../components/FormConfigurations/FormConfigurations.const";
import { ErrorToast } from "../../../components/toastMessage";
import { useGetFieldLabel } from "../../../contexts/LanguageContext";
import { useAlreadyCreatedFormConfigs } from "../../../hooks/form-step-config.hooks";
import {
  useGetForm,
  useGetFormIdByType,
  useServiceTypes,
} from "../../../hooks/forms.hooks";

type StepConfigFormProps = {
  ctx: "add" | "edit";
  onSubmit: (_: {
    forms: number[];
    serviceName: string;
    active: boolean;
  }) => unknown;
  defaultValues: {
    serviceName: string;
    active: boolean;
    selectedForms: {
      id?: number;
      type?: string;
    }[];
  };
};

function SelectedStep({
  type,
  serviceName,
  id,
  setStep,
}: {
  type?: string;
  serviceName?: string;
  id?: number;
  setStep: (_: { id: number; type: string }) => void;
}) {
  const convert = useGetFieldLabel();
  const {
    data: result,
    isFetching: isFetching1,
    isError: isError1,
  } = useGetFormIdByType(type, serviceName, !id);

  const {
    isFetching: isFetching2,
    data: formInfo,
    isError: isError2,
  } = useGetForm(!id ? (isFetching1 ? undefined : result) : id, "view");

  useEffect(() => {
    if ((!id || !type) && formInfo) {
      setStep({ id: formInfo.id, type: formInfo.type });
    }
  }, [id, formInfo, type]);

  const isLoading = isFetching1 || isFetching2;
  const isError = isError1 || isError2 || (!isLoading && !formInfo?.id);

  if (!type && !id) {
    return null;
  }

  if (isLoading || isError) {
    return (
      <div className="flex justify-center items-center p-8">
        {isError || isError1 ? (
          <p>Something went wrong while fetching info!</p>
        ) : (
          <SpinnerCircular />
        )}
      </div>
    );
  }

  return (
    <div className="grow overflow-auto p-8">
      <h4 className="text-2xl font-semibold mb-8">{type}</h4>
      <div className="grid grid-cols-2 auto-rows-auto gap-8">
        {formInfo?.fieldsEnabled?.map((e, i) => (
          <Input
            disabled
            label={convert(e)}
            placeholder={e.placeholder || ""}
            key={`field-${e.id}`}
            containerClass="col-span-2 lg:col-span-1"
          />
        ))}
        {formInfo?.documentsEnabled?.map((e) => (
          <div
            className="flex space-x-4 items-center col-span-2"
            key={`document-${e.document_config_id}`}
          >
            <p className="grow text-grey-600">
              {e.document_label}
              {e.mandatory ? <span className="text-red-500">*</span> : null}
            </p>
            <Input placeholder="Choose File" disabled containerClass="w-48" />
            <button>
              <i className="text-xl fa fa-eye"></i>
            </button>
            <button>
              <i className="text-xl fa fa-trash-o"></i>
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function StepConfigForm({
  defaultValues,
  onSubmit,
  ctx,
}: StepConfigFormProps) {
  const navigate = useNavigate();
  const isMutating = useIsMutating() > 0;
  const [selectedForms, setSelectedForms] = useState(
    defaultValues?.selectedForms || []
  );
  const [serviceName, setServiceName] = useState(
    defaultValues?.serviceName || ""
  );

  const { data: hasFormId } = useGetFormIdByType("Service Form", serviceName);

  const [active, setActive] = useState(defaultValues?.active || false);
  const { data: serviceTypeResult } = useServiceTypes();
  const { data: alreadyCreatedResult } = useAlreadyCreatedFormConfigs();
  const alreadyCreatedConfigs = alreadyCreatedResult || [];

  const serviceTypes = serviceTypeResult?.data || [];

  const last = selectedForms[selectedForms.length - 1];

  const onSubmitWrapper = () => {
    if (!serviceName) {
      return ErrorToast("Please select the service!");
    }
    if (!selectedForms.length) {
      return ErrorToast("Please add at least one step!");
    }

    const formsToSend = selectedForms
      .map((e) => e.id)
      .filter((e): e is NonNullable<typeof e> => !!e)
      .filter((e) => !Number.isNaN(e));

    if (formsToSend.length !== selectedForms.length) {
      return ErrorToast("Invalid Step!");
    }

    return onSubmit({ active, serviceName, forms: formsToSend });
  };

  return (
    <div className="p-4 md:p-8">
      <div className="flex-wrap md:flex-nowrap items-center flex justify-between mb-4 gap-4">
        <h4 className="grow text-xl lg:text-2xl font-bold">
          Step Configuration
        </h4>
        <div className="grow flex space-x-8 items-center">
          <label htmlFor="active" className="shrink-0 font-bold text-grey-600">
            Activate
          </label>
          <Switch
            id="active"
            checked={active}
            onChange={(e) => setActive(e.target.checked)}
          />
        </div>
        <button
          disabled={isMutating}
          onClick={onSubmitWrapper}
          className="py-2 px-6 font-semibold bg-primary text-white rounded-lg grow md:grow-0"
        >
          Save
        </button>
        <button
          disabled={
            isMutating || !selectedForms?.[selectedForms?.length - 1]?.type
          }
          className="disabled:bg-gray-300 py-2 px-6 font-semibold bg-blue-600 text-white rounded-lg grow md:grow-0"
          onClick={() => setSelectedForms((e) => e.concat({}))}
        >
          Add Step
        </button>
        <button
          disabled={isMutating}
          className="py-2 px-6 font-semibold bg-black text-white rounded-lg grow md:grow-0"
          onClick={() => navigate("/step-configurations")}
        >
          Close
        </button>
      </div>
      {/* <div className="bg-white rounded-3xl overflow-auto min-h-[500px] flex flex-col"> */}
      <div className="bg-white rounded-xl shadow-lg border border-gray-300 overflow-auto min-h-[500px] flex flex-col">
        <div className="flex items-center justify-between p-4 md:p-6 gap-4 shrink-0">
          <Select
            disabled={isMutating || ctx === "edit"}
            value={serviceName}
            containerClass="w-80"
            label="Select Service"
            onChange={(e) => {
              setServiceName(e.target.value);
              setSelectedForms([{ type: "Basic Information Form" }]);
            }}
          >
            <option disabled value="">
              Select Service
            </option>
            {serviceTypes.map((e) => (
              <option
                key={e.id}
                value={e.name}
                className="text-[#000] disabled:text-gray-400"
                disabled={alreadyCreatedConfigs.includes(e.name)}
              >
                {e.name}
              </option>
            ))}
          </Select>
          {selectedForms?.length > 1 && (
            <Select
              disabled={isMutating}
              label="Select Form"
              containerClass="w-80"
              value={last?.type || ""}
              onChange={(event) =>
                setSelectedForms((e) =>
                  e.map((a, i) => {
                    if (i === selectedForms.length - 1)
                      return { ...a, type: event.target.value };
                    return a;
                  })
                )
              }
            >
              <option disabled value="">
                Select Form
              </option>
              {formTypes.map((e) => (
                <option
                  key={e.value}
                  value={e.value}
                  className="text-[#000] disabled:text-gray-400"
                  disabled={
                    selectedForms.findIndex((o) => o.type === e.value) > -1 ||
                    (e.value === "Service Form" && !hasFormId)
                  }
                >
                  {e.label}
                </option>
              ))}
            </Select>
          )}
        </div>

        {!!last && (
          <div className="flex border-t border-gray-200 grow flex-wrap md:flex-nowrap">
            <div className="basis-full md:basis-1/4 border-r border-gray-200">
              {selectedForms.map((e, i) => (
                <div key={e.id} className="flex items-center bg-primary mb-1">
                  <p className="font-semibold text-white text-lg grow px-5">{`Step ${
                    i + 1
                  }`}</p>
                  <button
                    disabled={isMutating}
                    onClick={() =>
                      setSelectedForms((x) => x.filter((_, i1) => i1 !== i))
                    }
                    className={`shrink-0 px-5 py-3 bg-primary ${
                      !i ? "pointer-events-none opacity-0" : ""
                    }`}
                  >
                    <i className="text-xl m-0 fa text-red-500 fa-trash-o"></i>
                  </button>
                </div>
              ))}
            </div>
            <div className="basis-full md:basis-3/4">
              <SelectedStep
                id={last.id}
                setStep={(val: { id: number; type: string }) =>
                  setSelectedForms((p) =>
                    p.map((e, i) => (i === selectedForms.length - 1 ? val : e))
                  )
                }
                serviceName={serviceName}
                type={last.type}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
